import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { 
  OrganisationDDQQuestion, 
  ReferenceDocument, 
  FrameworkCoverage,
  FrameworkName 
} from "@/types/framework-mapping";

// Organisation DDQ Questions
export const useOrganisationDDQ = (clientId: string) => {
  return useQuery({
    queryKey: ['organisation-ddq', clientId],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_organisation_ddq_questions', {
        p_client_id: clientId
      });
      if (error) throw error;
      return (data || []) as OrganisationDDQQuestion[];
    },
    enabled: !!clientId,
  });
};

// Save organisation DDQ response
export const useSaveOrganisationResponse = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({
      clientId,
      questionId,
      response,
      evidenceDescription,
      evidenceFiles,
    }: {
      clientId: string;
      questionId: string;
      response: string;
      evidenceDescription?: string | null;
      evidenceFiles?: { name: string; path: string; uploaded_at: string }[] | null;
    }) => {
      // CIR-2026-372: org-level (asset_id IS NULL) saves go through a SECURITY
      // DEFINER RPC that issues the upsert with the explicit partial-index
      // predicate `ON CONFLICT (client_id, question_id) WHERE asset_id IS NULL`.
      // A direct PostgREST upsert with onConflict='client_id,question_id' could
      // not match the partial unique index (PostgREST omits the WHERE predicate)
      // nor the 3-col UNIQUE constraint, raising 42P10 on every org save. The
      // RPC self-guards write authorisation (staff/service_role) server-side.
      // Not yet in generated types — cast per CIR-2026-296.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- RPC not yet in generated types (CIR-2026-372)
      const { error } = await (supabase as any).rpc('save_organisation_ddq_response', {
        p_client_id: clientId,
        p_question_id: questionId,
        p_response: response,
        p_evidence_description: evidenceDescription || null,
        p_evidence_files: evidenceFiles || null,
      });

      if (error) throw error;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ 
        queryKey: ['organisation-ddq', variables.clientId] 
      });
    },
  });
};

// Framework Coverage
export const useFrameworkCoverage = (clientId: string, framework: FrameworkName) => {
  return useQuery({
    queryKey: ['framework-coverage', clientId, framework],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_client_framework_coverage', {
        p_client_id: clientId,
        p_framework_name: framework
      });
      if (error) throw error;
      return (data || []) as FrameworkCoverage[];
    },
    enabled: !!clientId && !!framework,
  });
};

// Reference Documents List
export const useReferenceDocuments = () => {
  return useQuery({
    queryKey: ['reference-documents'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('reference_documents')
        .select('id, document_type, document_name, version, asset_type, frameworks_covered, status')
        .eq('status', 'active')
        .order('document_type')
        .order('document_name');
      
      if (error) throw error;
      return (data || []) as Omit<ReferenceDocument, 'file_content'>[];
    },
  });
};

// Single Reference Document with content
export const useReferenceDocument = (documentName: string, version?: string) => {
  return useQuery({
    queryKey: ['reference-document', documentName, version],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_reference_document', {
        p_document_name: documentName,
        p_version: version
      });
      if (error) throw error;
      return (data && data[0]) as ReferenceDocument | undefined;
    },
    enabled: !!documentName,
  });
};

// Check if user is SecuraPath staff via securapath_users table
export const useIsSecuraPathStaff = () => {
  return useQuery({
    queryKey: ['is-securapath-staff'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return false;
      const { data } = await supabase
        .from('securapath_users')
        .select('id')
        .eq('auth_user_id', user.id)
        .maybeSingle();
      return !!data;
    },
  });
};
